export type AppErrorKind =
  | 'validation'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'server'
  | 'network'
  | 'unknown';

export const APP_ERROR_KINDS: readonly AppErrorKind[] = [
  'validation',
  'auth',
  'forbidden',
  'notFound',
  'server',
  'network',
  'unknown',
] as const;

export interface ValidationAppError {
  kind: 'validation';
  message: string;
  /**
   * Optional backend-provided key used to map
   * validation errors to translated UI messages.
   * Not populated yet; reserved for future API contract updates.
   */
  errorKey?: string; // future backend-provided translation key
}

export interface AuthAppError {
  kind: 'auth';
  message: string;
}

export interface ForbiddenAppError {
  kind: 'forbidden';
  message: string;
}

export interface NotFoundAppError {
  kind: 'notFound';
  message: string;
}

export interface ServerAppError {
  kind: 'server';
  message: string;
  status?: number;
}

export interface NetworkAppError {
  kind: 'network';
  message: string;
}

export interface UnknownAppError {
  kind: 'unknown';
  message: string;
}

export type AppError =
  | ValidationAppError
  | AuthAppError
  | ForbiddenAppError
  | NotFoundAppError
  | ServerAppError
  | NetworkAppError
  | UnknownAppError;

export function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    APP_ERROR_KINDS.includes((value as { kind: AppErrorKind }).kind)
  );
}

export function getAppErrorMessage(
  error: AppError,
  fallback = 'Unknown error',
): string {
  const message = typeof error.message === 'string' ? error.message.trim() : '';
  return message.length > 0 ? message : fallback;
}
